'use client';

import { useEffect, useState } from 'react';
import { API_URL, oidcConfig } from './config';
import { beginLogin, completeLogin, decodeJwtClaims, isCallback } from './oidc';

/**
 * OAuth2 gate (§6c/§P7). Two modes behind one `Session` shape, so nothing
 * downstream changes:
 *  - **OIDC** (prod): when `NEXT_PUBLIC_OIDC_*` is configured, `signIn` runs the
 *    Authorization Code + PKCE flow; on the redirect back the code is exchanged
 *    for an access token and the API's `/auth/me` resolves the authoritative
 *    tenant from the verified token.
 *  - **Dev bridge** (local/CI): otherwise `signIn` mints an HS256 token from the
 *    API's `POST /auth/dev-token` (disabled once an OIDC issuer is configured).
 */
export interface Session {
  subject: string;
  tenantId: string;
  displayName: string;
  token: string;
}

const STORAGE_KEY = 'dgt.session';
const DEV_TENANT = 'demo';

/** A tenant that has a generated model — shown in the dev sign-in picker. */
export interface CatalogTenant {
  tenantId: string;
  modelCount: number;
  latestName: string;
  latestAt: string;
}

/** Fetch the tenant catalog (dev/demo only); empty on any error or in OIDC mode. */
export async function fetchCatalog(): Promise<CatalogTenant[]> {
  try {
    const res = await fetch(`${API_URL}/catalog/tenants`);
    if (!res.ok) return [];
    return (await res.json()) as CatalogTenant[];
  } catch {
    return [];
  }
}

export function useSession(): {
  session: Session | null;
  ready: boolean;
  error: string | null;
  signIn: (tenantId?: string) => void;
  signOut: () => void;
} {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oidc = oidcConfig();

    // Returning from the IdP: finish the PKCE exchange, then scrub the query.
    if (oidc && isCallback(params)) {
      completeLogin(oidc, params)
        .then(async (tokens) => {
          const next = await sessionFromOidc(tokens.access_token, tokens.id_token);
          persist(next);
          setSession(next);
        })
        .catch((err: Error) => setError(err.message))
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
          setReady(true);
        });
      return;
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setSession(JSON.parse(raw) as Session);
    } catch {
      /* ignore malformed storage */
    }
    setReady(true);
  }, []);

  const signIn = (tenantId?: string): void => {
    setError(null);
    const oidc = oidcConfig();
    if (oidc) {
      beginLogin(oidc).catch((err: Error) => setError(err.message));
      return;
    }
    // Dev bridge: mint an HS256 token from the API's dev-token endpoint for the
    // chosen tenant (defaults to `demo`).
    const tid = (tenantId ?? DEV_TENANT).trim() || DEV_TENANT;
    const subject = `${tid}-user`;
    void fetch(`${API_URL}/auth/dev-token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tenantId: tid, subject }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Sign-in failed (${res.status}). Is the API running in dev mode?`);
        const { token } = (await res.json()) as { token: string };
        const next: Session = { subject, tenantId: tid, displayName: `${tid} analyst`, token };
        persist(next);
        setSession(next);
      })
      .catch((err: Error) => setError(err.message));
  };

  const signOut = (): void => {
    window.localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  };

  return { session, ready, error, signIn, signOut };
}

/** The current bearer token, if signed in — read by the API client. */
export function currentToken(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session).token : null;
  } catch {
    return null;
  }
}

/** The current tenant id, if signed in — used to pick live vs demo data. */
export function currentTenant(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session).tenantId : null;
  } catch {
    return null;
  }
}

function persist(session: Session): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/**
 * Build a session from OIDC tokens. The API is the authority on the tenant: it
 * decodes the tenant claim from the verified access token via `/auth/me`. The
 * id_token is used only for a friendly display name (never for authorization).
 */
async function sessionFromOidc(accessToken: string, idToken?: string): Promise<Session> {
  const res = await fetch(`${API_URL}/auth/me`, { headers: { authorization: `Bearer ${accessToken}` } });
  if (!res.ok) {
    throw new Error(`Session setup failed (${res.status}). The token may be missing the tenant claim.`);
  }
  const { tenantId, subject } = (await res.json()) as { tenantId: string; subject?: string };
  const claims = idToken ? decodeJwtClaims(idToken) : {};
  const displayName = pickString(claims.name) ?? pickString(claims.email) ?? subject ?? 'User';
  return { subject: subject ?? 'user', tenantId, displayName, token: accessToken };
}

function pickString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}
